import { fetchEventSource } from '@fortaine/fetch-event-source';
import { logger, sanitizeError } from '@heidi/runtime/logger';

import { type SideBySideConsultNoteGenerationRequest } from '../../contracts';
import {
  tryHandleNoteGenerationPhaseEvent,
  tryHandleNoteSuggestionsEvent,
} from '../note-suggestions/parse';
import {
  PERSONALISATION_STREAM_CAPABILITY,
  type NoteGenerationPhaseFrame,
  type NoteSuggestionsFrame,
} from '../note-suggestions/types';
import { streamSchema, type StreamType } from '../side-by-side-note-gen/types';
import { getConsultNoteStreamConfig } from './stream-config';
import {
  NOTE_GENERATION_OUTCOME_CAPABILITY,
  tryHandleNoteGenerationFailedEvent,
  type NoteGenerationFailureFrame,
} from './terminal-failure';

type PersonalisationCapableSideBySideRequest = SideBySideConsultNoteGenerationRequest & {
  client_capabilities?: string[];
};

export interface StreamEventHandlers {
  onOpen?: (response: Response) => Promise<void> | void;
  onMessage?: (streamData: StreamType) => void;
  onNoteSuggestions?: (frame: NoteSuggestionsFrame) => void;
  onNoteGenerationPhase?: (frame: NoteGenerationPhaseFrame) => void;
  onNoteGenerationFailed?: (frame: NoteGenerationFailureFrame) => void;
  onClose?: () => void;
  onError?: (error: unknown) => void;
}

export interface StreamConfig {
  sessionId: string;
  consultNoteId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a consult note generation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export async function streamConsultNoteGenerationV2_2(
  config: StreamConfig,
  handlers: StreamEventHandlers = {}
): Promise<void> {
  const {
    sessionId,
    consultNoteId,
    signal,
    logPrefix = '[CONSULT_NOTE_STREAM]',
  } = config;

  const {
    onOpen,
    onMessage,
    onNoteSuggestions,
    onNoteGenerationPhase,
    onNoteGenerationFailed,
    onClose,
    onError,
  } = handlers;

  const stream = getConsultNoteStreamConfig();
  const token = await stream.getAuthToken();
  if (signal?.aborted) return;
  const requestHeaders = stream.buildSseHeaders(token);

  const clientCapabilities = [
    ...(onNoteGenerationPhase ? [PERSONALISATION_STREAM_CAPABILITY] : []),
    ...(onNoteGenerationFailed ? [NOTE_GENERATION_OUTCOME_CAPABILITY] : []),
  ];
  const reqBody: PersonalisationCapableSideBySideRequest = {
    consult_note_id: consultNoteId,
    ignore_backup: stream.isTranscriptionServiceV2Enabled(),
    // Tag shortened-demo notes so the backend can separate them from real
    // generations in the consult_note_generation event.
    demo_mode: stream.getDemoModeForSession(sessionId),
    // Advertise control protocols only for callers wired to handle them.
    // SBS candidates do not provide these handlers and remain on the legacy path.
    ...(clientCapabilities.length ? { client_capabilities: clientCapabilities } : {}),
  };

  return fetchEventSource(buildConsultNoteGenerationV2_2RequestPath(sessionId), {
    headers: requestHeaders,
    openWhenHidden: true,
    method: 'POST',
    body: JSON.stringify(reqBody),
    signal,
    onopen: (response) =>
      stream.handleStreamOpen({
        response,
        logPrefix,
        onOpen,
        onError,
        context: { consultNoteId },
      }),
    onmessage(ev) {
      if (tryHandleNoteGenerationFailedEvent(ev, onNoteGenerationFailed)) return;
      if (
        tryHandleNoteGenerationPhaseEvent(ev, onNoteGenerationPhase, logPrefix, {
          consultNoteId,
        })
      ) {
        return;
      }
      if (
        tryHandleNoteSuggestionsEvent(ev, onNoteSuggestions, logPrefix, {
          consultNoteId,
        })
      ) {
        return;
      }

      const message = ev.data;

      // Parse and validate the stream data using the schema
      const streamParseResult = streamSchema.safeParse(message);
      if (streamParseResult.error) {
        // PHI: `message` is the verbatim SSE frame and can carry generated
        // note text, and a zod error embeds the received value. Log the shape
        // instead — an issue's path and code, plus the frame length.
        logger.error(`${logPrefix} stream parse error`, {
          consultNoteId,
          issues: streamParseResult.error.issues.map((issue) => ({
            path: issue.path.join('.'),
            code: issue.code,
          })),
          rawMessageLength: message.length,
        });
        return;
      }

      try {
        onMessage?.(streamParseResult.data);
      } catch (error) {
        logger.error(`${logPrefix} onMessage handler error`, {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
          consultNoteId,
        });
      }
    },
    onclose() {
      logger.debug(`${logPrefix} stream closed`, { consultNoteId });
      try {
        onClose?.();
      } catch (error) {
        logger.error(`${logPrefix} onClose handler error`, {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
          consultNoteId,
        });
      }
    },
    onerror(err) {
      logger.error(`${logPrefix} stream error`, {
        errorName: err instanceof Error ? err.name : typeof err,
        error: sanitizeError(err),
        consultNoteId,
      });
      try {
        onError?.(err as unknown);
      } catch (error) {
        // The single-stream onError rethrows the stream error it just logged.
        if (error !== err) {
          logger.error(`${logPrefix} onError handler error`, {
            errorName: error instanceof Error ? error.name : typeof error,
            error: sanitizeError(error),
          });
        }
      }
      throw err; // throw error to stop the stream from retrying
    },
  });
}

export const buildConsultNoteGenerationV2_2RequestPath = (sessionId: string) => {
  return `${getConsultNoteStreamConfig().resolveV2_2BaseUrl()}sessions/${sessionId}/consult-note`;
};
